import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { AlertCircle, X } from "lucide-react"
import { useState } from "react"
import { differenceInDays } from "date-fns"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/stores/authStore"
import { BILLING_ENABLED } from "@/lib/appConfig"

interface Subscription {
  status: string
  trial_end: string | null
}

export function TrialBanner() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [dismissed, setDismissed] = useState(false)

  const { data: sub } = useQuery<Subscription>({
    queryKey: ["plans", "me"],
    queryFn: async () => {
      const { data } = await api.get("/api/plans/me")
      return data.data
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  })

  if (!BILLING_ENABLED) return null
  if (dismissed) return null
  if (!sub) return null

  // Show trial banner
  if (sub.status === "trialing" && sub.trial_end) {
    const daysLeft = differenceInDays(new Date(sub.trial_end), new Date())
    if (daysLeft < 0 || daysLeft > 14) return null

    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-sm shrink-0">
        <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="flex-1 text-amber-800">
          {daysLeft <= 0
            ? "Your free trial has ended."
            : `Your free trial ends in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`}{" "}
          Add a payment method to keep your features.
        </span>
        <Button
          size="sm"
          className="h-7 text-xs rounded-lg shrink-0"
          onClick={() => navigate("/pricing")}
        >
          Subscribe
        </Button>
        <button onClick={() => setDismissed(true)} className="text-amber-600 hover:text-amber-800">
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  // Show payment failed banner
  if (sub.status === "past_due") {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-red-50 border-b border-red-200 text-sm shrink-0">
        <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
        <span className="flex-1 text-red-800">
          Payment failed. Please update your payment method to restore full access.
        </span>
        <Button
          size="sm"
          variant="destructive"
          className="h-7 text-xs rounded-lg shrink-0"
          onClick={() => navigate("/settings")}
        >
          Fix Now
        </Button>
        <button onClick={() => setDismissed(true)} className="text-red-600 hover:text-red-800">
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  return null
}
